/**
 * Shared rotation utilities for playlist item rotation.
 *
 * Centralises the logic that was previously duplicated across
 * SlideshowPage, SimpleEmbeddedSlideshowComponent, and
 * SimplePlaylistDisplayComponent.
 *
 * Rotation periods:
 * - **Daily** (date-based schedules): HijriDate, GregorianDate, Always
 * - **Weekly** (time/prayer-based schedules): TimeWindow, CrossDayTimeWindow,
 *   PrayerWindow, CrossDayPrayerWindow, PrayerBased
 *
 * Algorithm (single item):  `itemIndex = (periodIndex - 1) % totalItems`
 * Algorithm (group):        `startIndex = ((periodIndex - 1) * groupSize) % totalItems`
 *                           then collect `groupSize` items with wrapping.
 */

import { ScheduleType } from "../types/schedule.types";

// ---------------------------------------------------------------------------
// ISO week number
// ---------------------------------------------------------------------------

/**
 * Returns the ISO-8601 week number (1-53) for a given date.
 *
 * ISO weeks start on Monday.  Week 1 is the week containing the first
 * Thursday of the year.
 */
export function getISOWeekNumber(date: Date = new Date()): number {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  // Set to nearest Thursday: current date + 4 − current day number (Mon=1..Sun=7)
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return weekNo;
}

// ---------------------------------------------------------------------------
// Period index helpers
// ---------------------------------------------------------------------------

/** Schedule types that rotate daily (date-based). */
const DAILY_SCHEDULE_TYPES = new Set<string>([
  "HijriDate",
  "GregorianDate",
  "Always",
]);

/** Schedule types that rotate weekly (time/prayer-based). */
const WEEKLY_SCHEDULE_TYPES = new Set<string>([
  "TimeWindow",
  "CrossDayTimeWindow",
  "PrayerWindow",
  "CrossDayPrayerWindow",
  "PrayerBased",
]);

/** Return whether rotating this schedule type cycles daily. */
export function isDailyRotationType(scheduleType: string): boolean {
  return DAILY_SCHEDULE_TYPES.has(scheduleType);
}

/** Return whether rotating this schedule type cycles weekly. */
export function isWeeklyRotationType(scheduleType: string): boolean {
  return WEEKLY_SCHEDULE_TYPES.has(scheduleType);
}

interface HijriDateComponents {
  day: number;
  [key: string]: any;
}

interface PrayerTimesLike {
  HijriDateComponents?: HijriDateComponents;
  [key: string]: any;
}

function getStartOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseRotationStartDate(
  rotationStartDate?: string | null,
): Date | null {
  if (!rotationStartDate) {
    return null;
  }

  const datePart = rotationStartDate.slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function getAnchoredPeriodIndex(
  scheduleType: string,
  rotationStartDate?: string | null,
): number | null {
  const anchorDate = parseRotationStartDate(rotationStartDate);
  if (!anchorDate) {
    return null;
  }

  const today = getStartOfLocalDay(new Date());
  const startDate = getStartOfLocalDay(anchorDate);
  const elapsedDays = Math.max(
    0,
    Math.floor((today.getTime() - startDate.getTime()) / 86400000),
  );

  if (isWeeklyRotationType(scheduleType)) {
    return Math.floor(elapsedDays / 7) + 1;
  }

  return elapsedDays + 1;
}

/**
 * Determine the *period index* used for the modulo rotation.
 *
 * For daily schedules the index maps to day-of-month or day-of-year.
 * For weekly schedules the index is the ISO week number.
 *
 * @returns A positive integer period index, or `null` when a rotation
 *          cannot be determined (e.g. missing prayer data for HijriDate).
 */
export function getRotationPeriodIndex(
  scheduleType: string | ScheduleType,
  prayerTimes?: PrayerTimesLike | null,
  rotationStartDate?: string | null,
): number | null {
  const st =
    typeof scheduleType === "number"
      ? ScheduleType[scheduleType]
      : String(scheduleType);

  const anchoredPeriodIndex = getAnchoredPeriodIndex(st, rotationStartDate);
  if (anchoredPeriodIndex !== null) {
    return anchoredPeriodIndex;
  }

  // Daily rotation types
  if (st === "HijriDate") {
    return prayerTimes?.HijriDateComponents?.day ?? null;
  }
  if (st === "GregorianDate") {
    return new Date().getDate();
  }
  if (st === "Always") {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    return Math.floor(
      (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
  }

  // Weekly rotation types
  if (WEEKLY_SCHEDULE_TYPES.has(st)) {
    return getISOWeekNumber();
  }

  return null;
}

// ---------------------------------------------------------------------------
// Rotation index
// ---------------------------------------------------------------------------

/**
 * Given a playlist-like object (must have `isDailyRotation`, `scheduleType`,
 * and items with length), return the **0-based** start index for the rotation
 * group, or `null` if rotation is not applicable.
 *
 * When `rotationGroupSize` is 1 (default), this behaves the same as before:
 * `itemIndex = (periodIndex - 1) % totalItems`.
 *
 * When `rotationGroupSize` > 1, the start index is:
 * `startIndex = ((periodIndex - 1) * groupSize) % totalItems`.
 */
export function getRotationIndex(
  playlist: {
    isDailyRotation?: boolean;
    rotationGroupSize?: number;
    rotationStartDateUtc?: string | null;
    assignmentRotationStartDateUtc?: string | null;
    scheduleType: string | ScheduleType;
  },
  totalItems: number,
  prayerTimes?: PrayerTimesLike | null,
): number | null {
  if (!playlist.isDailyRotation || totalItems <= 0) return null;

  const periodIndex = getRotationPeriodIndex(
    playlist.scheduleType,
    prayerTimes,
    playlist.rotationStartDateUtc ?? playlist.assignmentRotationStartDateUtc,
  );
  if (periodIndex === null) return null;

  const groupSize = Math.max(1, playlist.rotationGroupSize ?? 1);

  if (groupSize === 1) {
    // Original single-item formula
    return (periodIndex - 1) % totalItems;
  }

  // Group formula: advance by groupSize items each period
  return ((periodIndex - 1) * groupSize) % totalItems;
}

/**
 * Return an array containing only the rotated item(s), or the original
 * array unchanged when rotation is not active.
 *
 * When `rotationGroupSize` is 1, returns a single-element array (original
 * behaviour). When > 1, returns `groupSize` items starting from the
 * rotation start index, wrapping around the end of the array.
 *
 * When `groupSize >= totalItems`, effectively returns all items (rotation
 * is a no-op).
 */
export function getRotatedItems<T>(
  playlist: {
    isDailyRotation?: boolean;
    rotationGroupSize?: number;
    rotationStartDateUtc?: string | null;
    assignmentRotationStartDateUtc?: string | null;
    scheduleType: string | ScheduleType;
    name?: string;
  },
  sortedItems: T[],
  prayerTimes?: PrayerTimesLike | null,
  logPrefix = "[Rotation]",
): T[] {
  const groupSize = Math.max(1, playlist.rotationGroupSize ?? 1);

  // If group size >= total items, just return all items (no rotation needed)
  if (playlist.isDailyRotation && groupSize >= sortedItems.length) {
    return sortedItems;
  }

  const startIdx = getRotationIndex(playlist, sortedItems.length, prayerTimes);
  if (startIdx === null) return sortedItems;

  const st =
    typeof playlist.scheduleType === "number"
      ? ScheduleType[playlist.scheduleType]
      : String(playlist.scheduleType);
  const rotationKind = isDailyRotationType(st) ? "daily" : "weekly";

  const periodIndex = getRotationPeriodIndex(
    playlist.scheduleType,
    prayerTimes,
    playlist.rotationStartDateUtc ?? playlist.assignmentRotationStartDateUtc,
  );

  // Collect groupSize items, wrapping around the array
  const result: T[] = [];
  for (let i = 0; i < groupSize; i++) {
    result.push(sortedItems[(startIdx + i) % sortedItems.length]);
  }

  console.log(`${logPrefix} ${rotationKind} rotation for "${playlist.name}":`, {
    scheduleType: st,
    rotationKind,
    periodIndex,
    totalItems: sortedItems.length,
    groupSize,
    startIndex: startIdx,
    selectedCount: result.length,
    selectedTitles: result.map(
      (item) => (item as any)?.assetTitle ?? (item as any)?.title,
    ),
  });

  return result;
}
